import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { bookings } from '@repletoryreach/sdk-api';
import { Badge, Button, Card, Group, Stack, Text, Skeleton } from '@repletoryreach/sdk-ui';
import { Alert, EmptyState, PageHeader } from '@repletoryreach/sdk-ui/data-display';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import { Checkbox, NumberInput, Select, Switch, Textarea, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { HelpButton } from '../../../app/guides/HelpButton';
import { useT } from '../../../i18n/I18nProvider';
import { useIsOrgAdmin } from '../../../app/components/RoleGate';
import { BOOKINGS_KEY, bookingErrorText, serviceColour, useDateLocale, useOrgTimezone, useServices, useStaff } from '../lib/data';
import {
  addDays, daysFrom, formatLocalDate, isoWeekday, localDateOf, localTimeOf, rangeIso, startOfWeek, todayIn, zonedIso
} from '../lib/time';
import '../bookings.css';

const MUTED = 'var(--rr-color-text-muted)';

type Editing = { kind: 'new' } | { kind: 'edit'; session: bookings.Session } | { kind: 'cancel'; session: bookings.Session } | null;

export default function BookingClassesPage() {
  const t = useT();
  const nav = useNavigate();
  const tz = useOrgTimezone();
  const locale = useDateLocale();
  const canEdit = useIsOrgAdmin();
  const today = todayIn(tz);
  const [week, setWeek] = useState(() => startOfWeek(today));
  const [serviceId, setServiceId] = useState('');
  const [editing, setEditing] = useState<Editing>(null);
  const servicesQ = useServices(true);
  const staffQ = useStaff();
  const classes = (servicesQ.data?.data ?? []).filter((s) => s.kind === 'class');
  const staff = staffQ.data?.data ?? [];
  const range = rangeIso(week, addDays(week, 7), tz);

  const q = useQuery({
    queryKey: [BOOKINGS_KEY, 'sessions', range.from, serviceId],
    queryFn: () => bookings.listSessions({ from: range.from, to: range.to, service_id: serviceId || undefined }),
    enabled: classes.length > 0,
    placeholderData: (prev) => prev
  });
  const sessions = [...(q.data?.data ?? [])].sort((a, b) => a.starts_at.localeCompare(b.starts_at));
  const byDay = useMemo(() => {
    const m = new Map<string, bookings.Session[]>();
    for (const s of sessions) {
      const d = localDateOf(s.starts_at, tz);
      m.set(d, [...(m.get(d) ?? []), s]);
    }
    return m;
  }, [sessions, tz]);

  const serviceOf = (id: string) => classes.find((s) => s.id === id);
  const staffName = (id: string | null) => (id ? staff.find((p) => p.id === id)?.name : null);

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('bookings.classes.title')}
        description={t('bookings.classes.description')}
        withDivider={false}
        actions={(
          <Group gap="sm">
            <HelpButton guide="bookings" />
            {canEdit && classes.length > 0 && <Button size="sm" data-guide="bookings-classes-add" onClick={() => setEditing({ kind: 'new' })}>{t('bookings.classes.new')}</Button>}
          </Group>
        )}
      />
      {!canEdit && <Alert color="info">{t('bookings.readOnly')}</Alert>}

      {servicesQ.isSuccess && classes.length === 0 ? (
        <Card withBorder data-guide="bookings-classes-list">
          <EmptyState
            title={t('bookings.classes.noClassServices')}
            description={t('bookings.classes.noClassServicesHint')}
            action={<Button size="sm" onClick={() => nav('/bookings/services')}>{t('bookings.classes.toServices')}</Button>}
          />
        </Card>
      ) : (
        <Card withBorder data-guide="bookings-classes-list">
          <Stack gap="md">
            <Group gap="sm" justify="space-between" wrap="wrap">
              <Group gap="xs" wrap="wrap">
                <Button size="xs" variant="default" onClick={() => setWeek(startOfWeek(today))}>{t('bookings.calendar.today')}</Button>
                <Button size="xs" variant="default" aria-label={t('bookings.calendar.previous')} onClick={() => setWeek(addDays(week, -7))}>‹</Button>
                <Button size="xs" variant="default" aria-label={t('bookings.calendar.next')} onClick={() => setWeek(addDays(week, 7))}>›</Button>
                <Text role="subtitle">
                  {t('bookings.calendar.weekOf', {
                    from: formatLocalDate(week, locale, { day: 'numeric', month: 'short' }),
                    to: formatLocalDate(addDays(week, 6), locale, { day: 'numeric', month: 'short', year: 'numeric' })
                  })}
                </Text>
              </Group>
              <Select
                size="sm"
                aria-label={t('bookings.field.service')}
                value={serviceId}
                onChange={(e) => setServiceId(e.currentTarget.value)}
                data={[{ value: '', label: t('bookings.classes.allClasses') }, ...classes.map((s) => ({ value: s.id, label: s.name }))]}
              />
            </Group>

            {q.error && (
              <Alert color="error" role="alert">
                <Group gap="sm" justify="space-between" wrap="wrap">
                  <span>{bookingErrorText(t, q.error)}</span>
                  <Button size="xs" variant="default" onClick={() => void q.refetch()}>{t('action.retry')}</Button>
                </Group>
              </Alert>
            )}
            {q.isPending && <Skeleton height={120} />}
            {!q.isPending && sessions.length === 0 && !q.error && (
              <EmptyState
                title={t('bookings.classes.emptyWeek')}
                description={canEdit ? t('bookings.classes.emptyWeekHint') : undefined}
                action={canEdit ? <Button size="xs" onClick={() => setEditing({ kind: 'new' })}>{t('bookings.classes.new')}</Button> : undefined}
              />
            )}

            {daysFrom(week, 7).filter((d) => byDay.has(d)).map((d) => (
              <Stack key={d} gap="xs">
                <Text role="overline" c={MUTED}>{formatLocalDate(d, locale, { weekday: 'long', day: 'numeric', month: 'long' })}</Text>
                {byDay.get(d)!.map((s) => {
                  const svc = serviceOf(s.service_id);
                  const cancelled = !!s.cancelled_at;
                  return (
                    <Group key={s.id} gap="sm" justify="space-between" wrap="wrap" className="rr-bookings__row">
                      <Group gap="sm" wrap="nowrap">
                        <span className="rr-bookings__swatch" style={{ background: serviceColour(svc?.colour) }} aria-hidden />
                        <Stack gap={0}>
                          <Text role="bodySmall" fw={600} style={cancelled ? { textDecoration: 'line-through' } : undefined}>
                            {t('bookings.classes.sessionLine', { from: localTimeOf(s.starts_at, tz), to: localTimeOf(s.ends_at, tz), name: svc?.name ?? t('common.unknown') })}
                          </Text>
                          <Text role="caption" c={MUTED}>
                            {[staffName(s.staff_id), t('bookings.classes.booked', { booked: s.booked, capacity: s.capacity })].filter(Boolean).join(' · ')}
                          </Text>
                        </Stack>
                        {cancelled && <Badge size="xs" color="red" variant="light">{t('bookings.status.cancelled')}</Badge>}
                        {!cancelled && s.places_left === 0 && <Badge size="xs" variant="light">{t('bookings.classes.full')}</Badge>}
                        {s.series_id && <Badge size="xs" color="gray" variant="light">{t('bookings.classes.repeats')}</Badge>}
                      </Group>
                      {canEdit && !cancelled && (
                        <Group gap="xs">
                          <Button size="xs" variant="subtle" onClick={() => setEditing({ kind: 'edit', session: s })}>{t('action.edit')}</Button>
                          <Button size="xs" variant="subtle" color="red" onClick={() => setEditing({ kind: 'cancel', session: s })}>{t('bookings.classes.cancel')}</Button>
                        </Group>
                      )}
                    </Group>
                  );
                })}
              </Stack>
            ))}
            <Text role="caption" c={MUTED}>{t('bookings.calendar.zone', { zone: tz })}</Text>
          </Stack>
        </Card>
      )}

      {(editing?.kind === 'new' || editing?.kind === 'edit') && (
        <SessionModal
          session={editing.kind === 'edit' ? editing.session : null}
          classes={classes.filter((c) => c.active || (editing.kind === 'edit' && c.id === editing.session.service_id))}
          staff={staff.filter((p) => p.active)}
          tz={tz}
          defaultDate={week > today ? week : today}
          onClose={() => setEditing(null)}
        />
      )}
      {editing?.kind === 'cancel' && (
        <CancelSessionModal session={editing.session} name={serviceOf(editing.session.service_id)?.name ?? ''} tz={tz} onClose={() => setEditing(null)} />
      )}
    </Stack>
  );
}

/** A new session or series, or a change to one session. */
function SessionModal({ session, classes, staff, tz, defaultDate, onClose }: {
  session: bookings.Session | null;
  classes: bookings.Service[];
  staff: bookings.Staff[];
  tz: string;
  defaultDate: string;
  onClose: () => void;
}) {
  const t = useT();
  const qc = useQueryClient();
  const [serviceId, setServiceId] = useState(session?.service_id ?? classes[0]?.id ?? '');
  const [date, setDate] = useState(session ? localDateOf(session.starts_at, tz) : defaultDate);
  const [time, setTime] = useState(session ? localTimeOf(session.starts_at, tz) : '09:00');
  const [staffId, setStaffId] = useState(session?.staff_id ?? '');
  const service = classes.find((c) => c.id === serviceId);
  const [capacity, setCapacity] = useState<number | ''>(session?.capacity ?? service?.capacity ?? 10);
  const [repeat, setRepeat] = useState(false);
  const [weekdays, setWeekdays] = useState<number[]>(() => [isoWeekday(date)]);
  const [until, setUntil] = useState(addDays(defaultDate, 8 * 7));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session && service?.capacity) setCapacity(service.capacity);
  }, [serviceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const invalid = !serviceId || !/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time) || capacity === '' || capacity < 1
    || (repeat && (weekdays.length === 0 || until < date));

  const save = useMutation({
    mutationFn: async () => {
      const starts_at = zonedIso(date, time, tz);
      if (session) {
        return [(await bookings.updateSession(session.id, { starts_at, staff_id: staffId || null, capacity: capacity === '' ? undefined : capacity })).data];
      }
      return (await bookings.createSessions({
        service_id: serviceId,
        staff_id: staffId || null,
        starts_at,
        capacity: capacity === '' ? undefined : capacity,
        ...(repeat ? { repeat: { weekdays: [...weekdays].sort(), until } } : {})
      })).data;
    },
    onSuccess: (made) => {
      void qc.invalidateQueries({ queryKey: [BOOKINGS_KEY] });
      notifySuccess(session ? t('bookings.classes.toast.saved') : t('bookings.classes.toast.created', { count: made.length }), t('bookings.classes.title'));
      onClose();
    },
    onError: (e) => setError(bookingErrorText(t, e))
  });

  return (
    <Modal opened onClose={onClose} title={session ? t('bookings.classes.edit') : t('bookings.classes.new')} size="md">
      <form onSubmit={(e) => { e.preventDefault(); if (!invalid) save.mutate(); }}>
        <Stack gap="md">
          <Select
            label={t('bookings.field.service')}
            value={serviceId}
            onChange={(e) => setServiceId(e.currentTarget.value)}
            data={classes.map((c) => ({ value: c.id, label: c.name }))}
            disabled={!!session}
            required
          />
          <Group gap="sm" grow wrap="wrap" align="flex-start">
            <TextInput type="date" label={t('bookings.classes.date')} value={date} onChange={(e) => setDate(e.currentTarget.value)} required />
            <TextInput type="time" label={t('bookings.classes.time')} value={time} onChange={(e) => setTime(e.currentTarget.value)} required
              description={service ? t('bookings.services.minutes', { n: service.duration_minutes }) : undefined} />
          </Group>
          <Group gap="sm" grow wrap="wrap" align="flex-start">
            <Select
              label={t('bookings.field.person')}
              value={staffId}
              onChange={(e) => setStaffId(e.currentTarget.value)}
              data={[{ value: '', label: t('bookings.classes.noPerson') }, ...staff.map((p) => ({ value: p.id, label: p.name }))]}
            />
            <NumberInput label={t('bookings.services.capacity')} value={capacity} onChange={setCapacity} min={1} max={10000}
              description={session ? t('bookings.classes.capacityHint', { booked: session.booked }) : undefined} />
          </Group>
          {!session && (
            <Stack gap="xs">
              <Switch label={t('bookings.classes.repeat')} description={t('bookings.classes.repeatHint')} checked={repeat} onChange={setRepeat} />
              {repeat && (
                <>
                  <div className="rr-bookings__weekdays" role="group" aria-label={t('bookings.classes.weekdays')}>
                    {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                      <Checkbox
                        key={n}
                        label={t(`settings.hours.day.${['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'][n - 1]}`)}
                        checked={weekdays.includes(n)}
                        onChange={(on) => setWeekdays((w) => (on ? [...w, n] : w.filter((x) => x !== n)))}
                      />
                    ))}
                  </div>
                  <TextInput type="date" label={t('bookings.classes.until')} value={until} onChange={(e) => setUntil(e.currentTarget.value)}
                    description={t('bookings.classes.untilHint')}
                    error={until < date ? t('bookings.classes.error.until') : undefined} />
                </>
              )}
            </Stack>
          )}
          {error && <Alert color="error" role="alert">{error}</Alert>}
          <Group gap="sm" justify="flex-end">
            <Button variant="default" onClick={onClose}>{t('action.cancel')}</Button>
            <Button type="submit" disabled={invalid} loading={save.isPending}>{session ? t('action.saveChanges') : t('bookings.classes.create')}</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function CancelSessionModal({ session, name, tz, onClose }: { session: bookings.Session; name: string; tz: string; onClose: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const locale = useDateLocale();
  const [reason, setReason] = useState('');
  const [notify, setNotify] = useState(true);
  const run = useMutation({
    mutationFn: () => bookings.cancelSession(session.id, { reason: reason.trim() || undefined, notify_clients: notify }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [BOOKINGS_KEY] });
      notifySuccess(t('bookings.classes.toast.cancelled', { name }), t('bookings.classes.title'));
      onClose();
    },
    onError: (e) => notifyError(bookingErrorText(t, e), t('bookings.classes.title'))
  });
  const when = formatLocalDate(localDateOf(session.starts_at, tz), locale, { weekday: 'long', day: 'numeric', month: 'long' });
  return (
    <Modal opened onClose={onClose} title={t('bookings.classes.cancelTitle', { name })} size="sm">
      <Stack gap="md">
        <Text role="bodySmall">{t('bookings.classes.cancelBody', { when, time: localTimeOf(session.starts_at, tz), count: session.booked })}</Text>
        <Textarea label={t('bookings.field.reason')} value={reason} onChange={(e) => setReason(e.currentTarget.value)} minRows={2} maxLength={500}
          description={t('bookings.reasonHint')} />
        <Switch label={t('bookings.classes.notifyClients')} checked={notify} onChange={setNotify} />
        <Group gap="sm" justify="flex-end">
          <Button variant="default" onClick={onClose}>{t('action.back')}</Button>
          <Button color="red" loading={run.isPending} onClick={() => run.mutate()}>{t('bookings.classes.cancel')}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
